import { PromptCompileError } from '../contracts.js';
import {
  formatBoundedDeviceTaxonomy,
  formatDeviceExclusionList,
} from '../deviceTaxonomy.js';
import { SEEDED_PLOT_DEVICE_CATALOG } from '../../domain/plotDeviceCatalog.js';
import { fallbackWorldDevices } from '../fallbacks.js';
import { buildInfluenceBlock, OPERATION_GUARDRAILS } from '../influence.js';
import type { PromptRequest, PromptSourceBundle } from '../types.js';
import { baseRequest, type CompilerSpec } from './base.js';
import { buildWorldGenerationSections } from './contextPaths.js';
import { buildWorldContext } from '../filters.js';

const SPEC: CompilerSpec = {
  purpose: 'world_devices',
  maxTier: 1,
  allowedTiers: [0, 1],
  budget: { inputTokens: 2000, outputTokens: 3000 },
  provider: 'deepseek',
};

const TAXONOMY_BUDGET = 1200;
const EXCLUSION_BUDGET = 600;

export function compileWorldDevices(source: PromptSourceBundle): PromptRequest {
  if (!source.confirmedSnapshot || source.mode === 'creating') {
    throw new PromptCompileError(
      'world_devices requires a confirmed world snapshot (confirmedSnapshot: true, mode not creating)',
    );
  }

  const seed = source.generationSeed ?? `${source.worldId}-devices`;
  const world = buildWorldContext(source);
  const influence = buildInfluenceBlock('world_devices', world);
  const header = [
    'PURPOSE: world_devices',
    OPERATION_GUARDRAILS.world_devices,
    'Do not duplicate seeded or prior device ids listed below.',
    influence.text,
  ].join('\n');

  const taxonomy = formatBoundedDeviceTaxonomy(TAXONOMY_BUDGET);
  const exclusion = formatDeviceExclusionList(
    SEEDED_PLOT_DEVICE_CATALOG,
    source.priorGeneratedDevices ?? [],
    source.inactiveDevices,
    EXCLUSION_BUDGET,
  );

  const { tier0, tier1 } = buildWorldGenerationSections({
    source,
    taxonomyBlock: taxonomy,
    exclusionBlock: exclusion,
  });

  return baseRequest(
    source,
    SPEC,
    { tier0, tier1 },
    [],
    [],
    [],
    header,
    {
      local: () => fallbackWorldDevices(seed),
      retryOnValidationError: true,
      noKeyUsesLocal: true,
    },
  );
}

export const worldDevicesMaxTier = SPEC.maxTier;
